import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { Button } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { endpoints } from "@/lib/api/endpoints";
import { ApiError } from "@/lib/api/client";
import type {
  DeliveryTabGridEntry,
  DispatchBoardResponse,
  DispatchDriverShiftEndResponse,
} from "@/lib/api/dispatchTypes";
import { useErrorMessage } from "@/shared/errors";
import { useMoney } from "@/shared/ui";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { usePrivateToast } from "@/shared/usePrivateToast";
import {
  dispatchActions,
  isolate,
  orderDriversForPicker,
  type BillDispatch,
} from "@/modules/pos/lib/dispatchBoard";
import { dispatchQueryKeys } from "@/modules/pos/lib/useDispatchBoard";
import { useDispatchCommand } from "@/modules/pos/lib/useDispatchCommand";
import { assignBills, type AssignOutcome, type CommandIds } from "@/modules/pos/lib/assignDrivers";
import { avatarTone, initials, waitingTotal } from "@/modules/pos/lib/deliveryWaiting";
import { DeliverySidePanel } from "./DeliverySidePanel";

/**
 * What the panel reads off a bag: its number, its customer and what it still
 * owes. The Delivery tab hands it grid rows; the bill's own dispatch strip
 * (`BillDispatchStrip`) hands it the open invoice in the same shape.
 */
export type AssignPanelBill = Pick<
  DeliveryTabGridEntry,
  "invoiceId" | "dailySequence" | "customerName" | "amountDue" | "runningTotal"
>;

/**
 * «إسناد إلى مندوب» for one bag or many (B6 · P24, plan D-38) — the design's
 * "Assign to a driver" aside.
 *
 * Header: what is being sent («الطلب رقم 60 · نور» or «3 طلبات · 427»), one
 * chip per bag, then the staff drivers from the board — on shift first and
 * least busy (`orderDriversForPicker`), off shift greyed and unpickable (D-8;
 * the server refuses them anyway) — with "Available" or "On a run · N orders".
 * There is no limit on how many bags a driver takes (decision 6).
 *
 * Confirm sends ONE existing T2 command per bag, one after another, each with
 * its own UUIDv7 kept in the card's `CommandIds` for a retry
 * (`assignBills`). Every bag gets its own result: when they all went, a toast
 * and the panel closes; when some were refused, the panel stays on the ones
 * left, each with its reason, and the ones that went leave the selection.
 */
export function AssignDriversPanel({
  bills,
  lookup,
  board,
  ids,
  onAssigned,
  onClose,
}: {
  bills: readonly AssignPanelBill[];
  lookup: (invoiceId: string) => BillDispatch | undefined;
  board: DispatchBoardResponse | undefined;
  ids: CommandIds;
  /** The bags the driver was offered — the card drops them from its selection. */
  onAssigned: (invoiceIds: string[]) => void;
  onClose: () => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const money = useMoney();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const [picked, setPicked] = useState<string | null>(null);
  const shiftEnd = useDispatchCommand();
  const [armedEnd, setArmedEnd] = useState<string | null>(null);
  const [shiftError, setShiftError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The bags still to send, and the last run's word per bag. */
  const [left, setLeft] = useState<readonly AssignPanelBill[]>(bills);
  const [results, setResults] = useState<Map<string, { ok: boolean; text: string }>>(new Map());

  const drivers = orderDriversForPicker(board?.drivers ?? [], locale);
  const pickedDriver = drivers.find((driver) => driver.sellerId === picked) ?? null;
  const single = left.length === 1 ? left[0] : null;
  const singleCurrent = single ? (lookup(single.invoiceId)?.current ?? null) : null;
  const canWithdraw = single ? dispatchActions(lookup(single.invoiceId)).withdraw : false;
  const total = left.reduce((sum, bill) => sum + waitingTotal(bill), 0);

  const meta = single
    ? single.customerName?.trim()
      ? t((m) => m.dispatch.assignMetaOne, { sequence: single.dailySequence, name: isolate(single.customerName.trim()) })
      : t((m) => m.dispatch.assignMetaOneNoName, { sequence: single.dailySequence })
    : t(
        countedKey(left.length, {
          one: (m) => m.dispatch.assignMetaOneNoName,
          two: (m) => m.dispatch.assignMetaTwo,
          few: (m) => m.dispatch.assignMetaFew,
          many: (m) => m.dispatch.assignMeta,
        }),
        { count: left.length, amount: money(total), sequence: left[0]?.dailySequence ?? "" },
      );

  const resultText = (outcome: AssignOutcome, name: string): { ok: boolean; text: string } => {
    switch (outcome.status) {
      case "offered":
        return { ok: true, text: t((m) => m.dispatch.assignResultOffered, { name: isolate(name) }) };
      case "refused":
        return {
          ok: false,
          text: outcome.key ? t((m) => m.dispatch[outcome.key!]) : errorMessage(outcome.error),
        };
      case "failed":
        return { ok: false, text: errorMessage(outcome.error) };
      case "skipped":
        return { ok: false, text: t((m) => m.dispatch.assignResultSkipped) };
    }
  };

  const confirm = async () => {
    if (!pickedDriver || busy) return;
    setBusy(true);
    try {
      const outcomes = await assignBills(
        left.map((bill) => ({ invoiceId: bill.invoiceId, entry: lookup(bill.invoiceId) })),
        pickedDriver.sellerId,
        (path, body) => apiFetch(path, { method: "POST", body }),
        ids,
      );
      const offered = outcomes.filter((o) => o.status === "offered").map((o) => o.invoiceId);
      await queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
      if (offered.length > 0) onAssigned(offered);
      if (offered.length === outcomes.length) {
        const name = isolate(pickedDriver.name);
        toast.success(
          t(
            countedKey(offered.length, {
              one: (m) => m.dispatch.toastAssignedBagsOne,
              two: (m) => m.dispatch.toastAssignedBagsTwo,
              few: (m) => m.dispatch.toastAssignedBagsFew,
              many: (m) => m.dispatch.toastAssignedBags,
            }),
            { name, count: offered.length },
          ),
        );
        onClose();
        return;
      }
      const next = new Map<string, { ok: boolean; text: string }>();
      for (const outcome of outcomes) next.set(outcome.invoiceId, resultText(outcome, pickedDriver.name));
      setResults(next);
      setLeft((current) => current.filter((bill) => !offered.includes(bill.invoiceId)));
    } finally {
      setBusy(false);
    }
  };

  /* G24-M2: end a driver's shift, second tap to confirm (the sheet's rule). */
  const endShift = async (driverSellerId: string, name: string) => {
    if (armedEnd !== driverSellerId) {
      setArmedEnd(driverSellerId);
      return;
    }
    setShiftError(null);
    try {
      await shiftEnd.send<DispatchDriverShiftEndResponse>(
        `shift-end:${driverSellerId}`,
        endpoints.restaurant.dispatchDriverShiftEnd(driverSellerId),
        {},
      );
      if (picked === driverSellerId) setPicked(null);
      toast.success(t((m) => m.dispatch.toastShiftEnded, { name: isolate(name) }));
    } catch (error) {
      setShiftError(
        error instanceof ApiError && error.code === "DISPATCH_DRIVER_NOT_FOUND"
          ? t((m) => m.dispatch.errorDriverNotFound)
          : errorMessage(error),
      );
    } finally {
      setArmedEnd(null);
      void queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
    }
  };

  const withdraw = async () => {
    if (!singleCurrent || busy) return;
    setBusy(true);
    const key = `withdraw:${singleCurrent.id}`;
    try {
      await apiFetch(endpoints.restaurant.dispatchWithdraw(singleCurrent.id), {
        method: "POST",
        body: { id: ids.idFor(key), clientTimestamp: new Date().toISOString() },
      });
      ids.retire(key);
      await queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
      toast.success(t((m) => m.dispatch.toastWithdrawn, { name: isolate(singleCurrent.driverName) }));
      onClose();
    } catch (error) {
      // G24-L2: a definite refusal retires the id; a lost answer keeps it.
      if (error instanceof ApiError) ids.retire(key);
      setResults(new Map([[singleCurrent.invoiceId, { ok: false, text: errorMessage(error) }]]));
      void queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
    } finally {
      setBusy(false);
    }
  };

  const failedCount = [...results.values()].filter((r) => !r.ok).length;

  return (
    <DeliverySidePanel
      badge="driver"
      title={t((m) => m.dispatch.assignTitle)}
      subtitle={<bdi>{meta}</bdi>}
      onClose={onClose}
      testId="assign-drivers-panel"
      footer={
        <>
          <Button
            variant="outline"
            size="md"
            data-ui="delivery-panel-cancel"
            disabled={busy}
            onClick={onClose}
            data-testid="assign-drivers-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="md"
            data-ui="delivery-panel-confirm"
            disabled={!pickedDriver || busy || left.length === 0}
            isLoading={busy}
            onClick={() => void confirm()}
            data-testid="assign-drivers-confirm"
          >
            {pickedDriver
              ? t((m) => m.dispatch.assignConfirm, { name: isolate(pickedDriver.name) })
              : t((m) => m.dispatch.sheetPick)}
          </Button>
        </>
      }
    >
      <div data-ui="assign-chips" className="flex flex-wrap" data-testid="assign-drivers-chips">
        {left.map((bill) => (
          <span key={bill.invoiceId} data-ui="assign-chip" data-testid={`assign-chip-${bill.dailySequence}`}>
            <bdi dir="ltr">#{bill.dailySequence}</bdi> · {money(waitingTotal(bill))}
          </span>
        ))}
      </div>

      {results.size > 0 ? (
        <div data-ui="assign-results" role="status" data-testid="assign-drivers-results" className="flex flex-col">
          {failedCount > 0 ? <p data-ui="assign-results-lead">{t((m) => m.dispatch.assignPartial)}</p> : null}
          <ul className="m-0 flex list-none flex-col p-0">
            {[...results.entries()].map(([invoiceId, result]) => {
              const bill = bills.find((b) => b.invoiceId === invoiceId);
              return (
                <li
                  key={invoiceId}
                  data-ui="assign-result"
                  data-ok={result.ok ? "true" : "false"}
                  data-testid={`assign-result-${bill?.dailySequence ?? invoiceId}`}
                  className="flex items-start"
                >
                  <bdi dir="ltr" data-ui="assign-result-no">#{bill?.dailySequence}</bdi>
                  <span className="min-w-0 flex-1">{result.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <div data-ui="assign-drivers" className="flex flex-col">
        <span data-ui="assign-drivers-title">{t((m) => m.dispatch.assignStaffDrivers)}</span>
        {shiftError ? (
          <p data-ui="assign-drivers-note" data-tone="danger" role="alert" data-testid="assign-drivers-shift-error">
            {shiftError}
          </p>
        ) : null}
        {!board ? (
          <p data-ui="assign-drivers-note">{t((m) => m.dispatch.sheetLoadFailed)}</p>
        ) : drivers.length === 0 ? (
          <p data-ui="assign-drivers-note" data-testid="assign-drivers-none">
            {t((m) => m.dispatch.sheetNoDrivers)}
          </p>
        ) : (
          <div role="radiogroup" aria-label={t((m) => m.dispatch.assignStaffDrivers)} className="flex flex-col">
            {!drivers.some((driver) => driver.onShift) ? (
              <p data-ui="assign-drivers-note" data-tone="warning" data-testid="assign-drivers-none-on-shift">
                {t((m) => m.dispatch.sheetNoneOnShift)}
              </p>
            ) : null}
            {drivers.map((driver) => {
              const isCurrent = singleCurrent?.driverSellerId === driver.sellerId;
              const disabled = !driver.onShift || isCurrent || busy;
              const active = picked === driver.sellerId;
              const status = !driver.onShift
                ? t((m) => m.dispatch.sheetOffShift)
                : isCurrent
                  ? t((m) => m.dispatch.assignHasOffer)
                  : driver.activeCount > 0
                    ? t(
                        countedKey(driver.activeCount, {
                          one: (m) => m.dispatch.assignOnRunOne,
                          two: (m) => m.dispatch.assignOnRunTwo,
                          few: (m) => m.dispatch.assignOnRunFew,
                          many: (m) => m.dispatch.assignOnRun,
                        }),
                        { count: driver.activeCount },
                      )
                    : t((m) => m.dispatch.sheetOnShift);
              const endArmed = armedEnd === driver.sellerId;
              return (
                <div key={driver.sellerId} role="none" data-ui="assign-driver-row" className="flex items-stretch">
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={disabled}
                  data-ui="assign-driver"
                  data-active={active ? "" : undefined}
                  data-on-shift={driver.onShift ? "true" : "false"}
                  data-testid={`assign-drivers-${driver.sellerId}`}
                  onClick={() => {
                    setPicked(driver.sellerId);
                    setArmedEnd(null);
                  }}
                  className="flex min-w-0 flex-1 items-center text-start"
                >
                  <span data-ui="assign-avatar" data-tone={avatarTone(driver.sellerId)} aria-hidden="true">
                    {initials(driver.name)}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span data-ui="assign-driver-name" className="truncate">
                      <bdi>{driver.name}</bdi>
                    </span>
                    <span
                      data-ui="assign-driver-status"
                      data-state={!driver.onShift ? "off" : driver.activeCount > 0 || isCurrent ? "busy" : "free"}
                      className="flex items-center"
                    >
                      <span aria-hidden="true" data-ui="assign-driver-dot" />
                      <span>{status}</span>
                    </span>
                  </span>
                  <span aria-hidden="true" data-ui="assign-radio" />
                </button>
                {/* G24-M2 — «إنهاء الوردية» from the till, for the phone that
                    died and left its driver «متاح»: beside the option, never in
                    it; the second tap confirms. */}
                {driver.onShift ? (
                  <button
                    type="button"
                    data-ui="assign-end-shift"
                    data-armed={endArmed ? "true" : "false"}
                    disabled={busy || shiftEnd.busy !== null}
                    data-testid={`assign-drivers-end-shift-${driver.sellerId}`}
                    onClick={() => void endShift(driver.sellerId, driver.name)}
                  >
                    {endArmed ? t((m) => m.dispatch.sheetEndShiftConfirm) : t((m) => m.dispatch.sheetEndShift)}
                  </button>
                ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {canWithdraw && singleCurrent ? (
        <button
          type="button"
          data-ui="assign-withdraw"
          disabled={busy}
          onClick={() => void withdraw()}
          data-testid="assign-drivers-withdraw"
          className="self-start"
        >
          {t((m) => m.dispatch.sheetWithdraw)}
        </button>
      ) : null}

      <p data-ui="assign-note" className="flex items-start">
        <Info aria-hidden="true" size={14} strokeWidth={1.75} className="shrink-0" />
        <span>{t((m) => m.dispatch.assignPlatformNote)}</span>
      </p>
    </DeliverySidePanel>
  );
}

